<template>
  <v-list dense style="padding-top: 0px !important;" flat>
    <v-subheader>
      {{ facility.name.toLowerCase() }} info
    </v-subheader>
    <template v-for="tab in this.detailTabs">
      <v-list-item :key="tab.key"
                   :to="tab.to ? tab.to : null" :href="tab.href ? tab.href : null">
        <v-list-item-icon>
          <v-icon dense>{{ tab.icon }}</v-icon>
        </v-list-item-icon>
        <v-list-item-title>{{
            tab.label.toLowerCase()
          }}
        </v-list-item-title>
      </v-list-item>
    </template>
    <template v-for="tab in this.otherMaps">
      <v-list-item v-if="showMapLinks && ['ACTIVE', 'CONSTRUCT'].indexOf(facility.status) > -1" :key="tab.key"
                   :to="tab.to ? tab.to : null" :href="tab.href ? tab.href : null">
        <v-list-item-icon>
          <v-icon dense>{{ tab.icon }}</v-icon>
        </v-list-item-icon>
        <v-list-item-title>{{
            tab.label.toLowerCase()
          }}
        </v-list-item-title>
      </v-list-item>
    </template>
  </v-list>
</template>

<script>
import gql from "graphql-tag";
import ExtentUtils from "@/utils/ExtentUtils";

export default {
  name: "FacilityLinksDetails",
  props: {
    facilityId: {
      type: String,
      required: true
    }
  },

  data: () => ({
    facility: {facilityId: '', name: '', status: ''},
    showMapLinks: false
  }),

  watch: {
    async $route() {
      this.showMapLinks = await this.checkExtent(this.facilityId)
    }
  },

  async mounted() {
    this.showMapLinks = await this.checkExtent(this.facilityId)
  },

  methods: {
    checkExtent: async (facilityId) => {
      const result = await ExtentUtils.getFacilityExtent(facilityId).then((extent) => extent)
      return result ? true : false
    }
  },

  computed: {
    showMaps() {
      return this.facility.status === 'ACTIVE' || this.facility.status === 'CONSTRUCT'
    },

    detailTabs() {
      let tabs = [
        {
          label: "DETAILS",
          icon: "mdi-information",
          to: {
            path: `/facilities/buildings/${this.facilityId}/details`
          },
          key: 'details',
          title: 'Building Details'
        },
        {
          label: "ADDITIONS",
          icon: "mdi-table-search",
          to: {
            path: `/facilities/buildings/${this.facilityId}/additions`
          },
          key: 'additions',
          title: 'Building Additions'
        },
        {
          label: "ADDRESSES",
          icon: "mdi-table-search",
          to: {
            path: `/facilities/buildings/${this.facilityId}/addresses`
          },
          key: 'addresses',
          title: 'Buildiing Addresses'
        },
        {
          label: "PROJECTS",
          icon: "mdi-table-search",
          to: {
            path: `/facilities/buildings/${this.facilityId}/projects`
          },
          key: 'projects',
          title: 'Building Projects'
        },
        {
          label: "DOCUMENTS",
          icon: "mdi-file-search",
          to: {
            path: `/facilities/buildings/${this.facilityId}/documents`
          },
          key: 'documents',
          title: 'Building documents (Meridian Explorer)'
        }
      ]

      if (this.showMapLinks) {
        tabs = tabs.concat([
          {
            label: "MAP (new)",
            icon: "mdi-map-marker-radius",
            secure: false,
            to: {
              path: `/facilities/buildings/${this.facilityId}/map`
            },
            key: 'map',
            title: "The Campus map does not require you to log in and does not contain as much detail as the other secured maps."
          }
        ])
      }

      return tabs
    },

    otherMaps() {
      return [
        {
          label: "ASSETS MAP (external)",
          icon: "mdi-map-marker-radius",
          secure: true,
          href: `https://apps.gis.msu.edu/facilities-information-tool/maps/assets/?find=${this.facilityId}&l=0`,
          title: 'This app is heavily focused on building assets maintained by IPF and allows you to look at assets, floor-by-floor, search for assets and execute queries for groups of assets.'
        },
        {
          label: "CAMPUS MAP (external)",
          icon: "mdi-map-marker-radius",
          secure: false,
          href: `https://apps.gis.msu.edu/facilities-information-tool/maps/campus/?query=buildings,BUILDINGID,${this.facilityId}&l=0`,
          title: 'This map contains generally available facility information for anyone and does not require a login or special access.'
        },
        {
          label: "INFRASTRUCTURE MAP (external)",
          icon: "mdi-map-marker-radius",
          secure: true,
          href: `https://apps.gis.msu.edu/facilities-information-tool/maps/infrastructure/?query=buildingsActive,BUILDINGID,${this.facilityId}&l=0`,
          title: 'This application is focused on infrastructure outside the building such as underground utilities, plants (managed trees and shrubs), stormwater best management practices and more.'
        },
        {
          label: "IPF INDOORS (external)",
          icon: "mdi-navigation-variant",
          secure: true,
          href: `https://gis.msu.edu/portal/apps/indoors?appid=86ebb70206df4ddcaf6250f540fecb58&itemUniqueIdField=FACILITY_ID&itemSourceKey=Facilities&itemUniqueId=${this.facilityId}&l=0`,
          title: 'Heavily focused on navigating, with room level directions, to assets, work orders, spaces, and more. This application is integrated with AppTree to quickly locate your work and is primarily intended for IPF staff and IPF campus partners.'
        },
        {
          label: "MSU INDOORS (external)",
          icon: "mdi-navigation-variant",
          secure: false,
          href: `https://gis.msu.edu/portal/apps/indoors?appid=7409d0452fed4aa689de59f0feee4946&itemUniqueIdField=FACILITY_ID&itemSourceKey=Facilities&itemUniqueId=${this.facilityId}&l=0`,
          title: 'The MSU Indoors application allows does not require you to log in and has information primarily intended public users and is primarily focused on focused on navigating, with room level directions, to campus features such as personal health spaces, class locations, points of interest, and more.'
        }
      ]
    }
  },

  apollo: {
    facility: {
      query: gql`query ($facId: String!){
          facility(where: {facilityId: {eq: $facId}}){
              facilityId
              name
              building
              status
          }
      }`,
      update(data) {
        let facility = null
        if (data.facility[0]) {
          facility = data.facility[0]
        }
        return facility
      },
      variables() {
        return {
          facId: this.facilityId
        }
      }
    }
  }
}
</script>

<style scoped>
.v-list-item__icon {
  height: 24px;
  margin: 0px !important;
  margin-right: .5em !important;
}

.v-list--dense .v-list-item, .v-list-item--dense {
  min-height: 20px;
  height: 24px;
}

.v-list-item__title {
  font-weight: 400 !important;
}
</style>
